import { Icon } from "@/styles/Icon";
import styled from "styled-components";

interface CheckboxComponentProps {
  id: string;
  type?: 'checkbox' | 'radio';
  name?: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  children: React.ReactNode;
  fontSize?: string | number;
  marginBottom?: string | number;
  width?: number;
  onViewClick?: () => void;
  variant?: 'default' | 'selectAll' | 'white';
}

export const CheckboxComponent: React.FC<CheckboxComponentProps> = ({
  id,
  type = 'checkbox',
  name,
  checked,
  onChange,
  children,
  fontSize,
  marginBottom,
  width,
  onViewClick,
  variant = 'default'
}) => (
  <CheckboxWrapper $marginBottom={marginBottom} $width={width}>
    <HiddenInput
      type={type}
      id={id}
      name={name}
      checked={checked}
      onChange={onChange}
    />
    <Label 
      htmlFor={id} 
      $fontSize={fontSize} 
      $variant={variant}
    >
      <CheckboxIcon>
        <Icon 
          name={
            checked 
              ? (variant === 'white' ? 'checkedWhite' : 'checked')
              : (variant === 'white' ? 'uncheckedWhite' : 'unchecked')
          } 
          size={20}
        />
      </CheckboxIcon>
      <LabelContent>{children}</LabelContent>
      {onViewClick && (
        <ViewButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onViewClick();
          }}
        >
          <span>View</span>
          <Icon name="arrowRight12" size={12} />
        </ViewButton>
      )}
    </Label>
  </CheckboxWrapper>
);

export default CheckboxComponent;

// 스타일 컴포넌트
const CheckboxWrapper = styled.div<{ $marginBottom?: string | number, $width?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ $marginBottom }) => 
    typeof $marginBottom === 'number' ? `${$marginBottom}px` : $marginBottom || '12px'};
  position: relative;
  width: ${({ $width }) => $width ? `${$width}px` : '100%'};
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 20px;
  height: 20px;
  cursor: pointer;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  margin: 0;
  z-index: 1;
`;

const CheckboxIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 12px;
  flex-shrink: 0;
`;

const Label = styled.label<{ 
  $fontSize?: string | number;
  $variant?: 'default' | 'selectAll' | 'white';
}>`
  font-size: ${({ $fontSize }) => 
    typeof $fontSize === 'number' ? `${$fontSize}px` : $fontSize || '16px'};
  color: ${({ theme }) => theme.colors.fontBlackText};
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  width: 100%;
  padding-left: 0;
  
  /* variant에 따른 스타일 분기 */
  ${({ $variant }) => $variant === 'selectAll' 
    ? `
      font-weight: 700;
      line-height: 20px;
    ` 
    : `
      font-weight: 400;
      line-height: normal;
    `
  }
`;

const LabelContent = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const ViewButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.fontGraySecondary};
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-left: auto;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    opacity: 0.8;
  }
`;